/**
 * Edit Word List Page
 * Edit existing word list
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { apiRequest } from '@/api/client';

interface WordListFormData {
  listName: string;
  classId: string;
  listType: string;
  words: string;
}

interface Class {
  classId: string;
  className: string;
  gradeLevel: string;
}

export const EditWordListPage = () => {
  const { wordListId } = useParams<{ wordListId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [formData, setFormData] = useState<WordListFormData>({
    listName: '',
    classId: '',
    listType: 'spelling',
    words: '',
  });
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<WordListFormData>>({});
  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, [wordListId]);

  useEffect(() => {
    // Update word count when words change
    const words = formData.words
      .split(/[\n,]+/)
      .map(w => w.trim())
      .filter(w => w.length > 0);
    setWordCount(words.length);
  }, [formData.words]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch classes
      const classesResponse = await apiRequest<{ classes: Class[] }>('get_teacher_classes', {
        userEmail: user?.email,
      });

      if (classesResponse.success && (classesResponse as any).classes) {
        setClasses((classesResponse as any).classes);
      }

      // Fetch word list
      const wordListResponse = await apiRequest<any>('get_wordlist', {
        listId: wordListId,
        userEmail: user?.email,
      });

      if (wordListResponse.success && (wordListResponse as any).wordList) {
        const wordList = (wordListResponse as any).wordList;
        setFormData({
          listName: wordList.listName || '',
          classId: wordList.classId || '',
          listType: wordList.listType || 'spelling',
          words: (wordList.words || []).join('\n'),
        });
      } else {
        showNotification('error', 'Word list not found');
        navigate('/wordlists');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showNotification('error', 'Failed to load word list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof WordListFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<WordListFormData> = {};

    if (!formData.listName.trim()) {
      newErrors.listName = 'List name is required';
    }

    if (!formData.classId) {
      newErrors.classId = 'Class is required';
    }

    if (!formData.words.trim()) {
      newErrors.words = 'At least one word is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Parse words from textarea
      const words = formData.words
        .split(/[\n,]+/)
        .map(w => w.trim())
        .filter(w => w.length > 0);

      const response = await apiRequest('update_wordlist', {
        userEmail: user?.email,
        listId: wordListId,
        listName: formData.listName,
        classId: formData.classId,
        listType: formData.listType,
        words: words,
      });

      if (response.success) {
        showNotification('success', 'Word list updated successfully');
        navigate(`/wordlists/${wordListId}`);
      } else {
        showNotification('error', response.error || 'Failed to update word list');
      }
    } catch (error) {
      console.error('Failed to update word list:', error);
      showNotification('error', 'Failed to update word list');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Edit Word List</h1>
        <p className="text-slate-600 mt-2">Update word list information</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* List Name */}
          <div>
            <label htmlFor="listName" className="block text-sm font-medium text-slate-700 mb-2">
              List Name *
            </label>
            <input
              type="text"
              id="listName"
              value={formData.listName}
              onChange={(e) => handleChange('listName', e.target.value)}
              className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.listName ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="e.g., Week 1 Spelling Words"
            />
            {errors.listName && (
              <p className="text-red-600 text-sm mt-1">{errors.listName}</p>
            )}
          </div>

          {/* Class Selection */}
          <div>
            <label htmlFor="classId" className="block text-sm font-medium text-slate-700 mb-2">
              Class *
            </label>
            <select
              id="classId"
              value={formData.classId}
              onChange={(e) => handleChange('classId', e.target.value)}
              className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.classId ? 'border-red-500' : 'border-slate-300'
              }`}
            >
              <option value="">Select a class</option>
              {classes.map((cls) => (
                <option key={cls.classId} value={cls.classId}>
                  {cls.className} ({cls.gradeLevel})
                </option>
              ))}
            </select>
            {errors.classId && (
              <p className="text-red-600 text-sm mt-1">{errors.classId}</p>
            )}
          </div>

          {/* List Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              List Type *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="listType"
                  value="spelling"
                  checked={formData.listType === 'spelling'}
                  onChange={(e) => handleChange('listType', e.target.value)}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-slate-700">Words (Spelling)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="listType"
                  value="phonics"
                  checked={formData.listType === 'phonics'}
                  onChange={(e) => handleChange('listType', e.target.value)}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-slate-700">Sounds (Phonics)</span>
              </label>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {formData.listType === 'spelling' 
                ? 'Words will appear naturally in stories' 
                : 'Multiple words containing these sound patterns will be included in stories'}
            </p>
          </div>

          {/* Words */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="words" className="block text-sm font-medium text-slate-700">
                {formData.listType === 'spelling' ? 'Words' : 'Sounds'} *
              </label>
              <span className="text-sm text-slate-500">
                {wordCount} {wordCount === 1 ? (formData.listType === 'spelling' ? 'word' : 'sound') : (formData.listType === 'spelling' ? 'words' : 'sounds')}
              </span>
            </div>
            <textarea
              id="words"
              value={formData.words}
              onChange={(e) => handleChange('words', e.target.value)}
              rows={10}
              className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono ${
                errors.words ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder={formData.listType === 'spelling' 
                ? "Enter words (one per line or comma-separated)"
                : "Enter sounds (one per line or comma-separated)\nExample:\nch\nsh\nth\noo\nai"}
            />
            {errors.words && (
              <p className="text-red-600 text-sm mt-1">{errors.words}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              Enter one {formData.listType === 'spelling' ? 'word' : 'sound'} per line or separate with commas
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Updating...' : 'Update Word List'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/wordlists/${wordListId}`)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
